<script setup lang="ts">
import { PhCopy } from '@phosphor-icons/vue';
import type { Component } from 'vue';
import { useI18n } from 'vue-i18n';

// The header names the thing the menu was opened for — and is now also the way to copy that name,
// which replaced the separate "Kopiera namn" entry every single-item menu used to carry.
//
// That trade costs discoverability: a labelled row announces itself, a clickable title does not.
// Hence the deliberate affordances here — pointer cursor, hover fill, and a copy glyph that fades in
// on hover — plus an aria-label, since the visible text is the item's name and says nothing about
// what activating it does.
//
// The `title` attribute stays the bare name rather than becoming a hint: names are ellipsised at
// 16rem, and revealing the full one on hover is the only way to read a long one.

const { t } = useI18n();

defineProps<{
  icon: Component;
  color: string;
  name: string;
}>();

const emit = defineEmits<{
  (e: 'copy-name'): void;
}>();
</script>

<template>
  <button
    type="button"
    class="context-menu-header"
    :aria-label="t('fileManager.contextMenu.copyNameAriaLabel', { name })"
    @click="emit('copy-name')"
  >
    <component :is="icon" :size="18" weight="fill" :color="color" />
    <span class="context-menu-header-name" :title="name">{{ name }}</span>
    <PhCopy :size="14" class="context-menu-header-copy" />
  </button>
</template>

<style scoped>
.context-menu-header {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  width: 100%;
  max-width: 16rem;
  padding: 0.5rem 0.4rem 0.3rem;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  font: inherit;
}

.context-menu-header:hover {
  background-color: #f5f5f5;
}

.context-menu-header-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-size: 0.8125rem;
  font-weight: 600;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* Reserves its space at all times so revealing it on hover never reflows the name beside it. */
.context-menu-header-copy {
  flex: none;
  opacity: 0;
  color: #6b6b6b;
  transition: opacity 0.12s ease-out;
}

.context-menu-header:hover .context-menu-header-copy,
.context-menu-header:focus-visible .context-menu-header-copy {
  opacity: 1;
}
</style>
